import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, asArray } from "@/lib/api";
import { fmtTime } from "@/lib/time";
import { toast } from "sonner";
import { TerminalSquare, Copy, Send, ChevronRight, Download, MonitorDown, Apple, Server as ServerIcon } from "lucide-react";
import { StatusBadge, KPI } from "@/components/Primitives";

const PLATFORMS = [
  { key: "windows", label: "Windows", icon: MonitorDown, hint: "Win 10/11 · Server 2019/2022" },
  { key: "macos", label: "macOS", icon: Apple, hint: "Apple Silicon · Intel" },
  { key: "linux", label: "Linux", icon: ServerIcon, hint: "Ubuntu · Debian · RHEL" },
];

function CopyRow({ label, value, testid }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">{label}</div>
      <div className="flex items-start gap-2">
        <code className="flex-1 bg-zinc-900/40 border border-zinc-900 rounded p-2 font-mono text-[12px] break-all" data-testid={testid}>{value}</code>
        <button onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }} className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-zinc-300 shrink-0"><Copy size={14} /></button>
      </div>
    </div>
  );
}

export default function Agents() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [agents, setAgents] = useState([]);
  const [actions, setActions] = useState([]);

  // deployment builder
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [clientId, setClientId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [batch, setBatch] = useState(true);
  const [platform, setPlatform] = useState("windows");
  const [macArch, setMacArch] = useState("arm64");
  const [bundle, setBundle] = useState(null);
  const [generating, setGenerating] = useState(false);

  // remote action
  const [pickedAgent, setPickedAgent] = useState("");
  const [action, setAction] = useState({ kind: "powershell", command: "Get-Service Spooler" });

  const load = async () => {
    const [s, a, ac] = await Promise.all([
      api.get("/agent/status").catch(() => ({ data: null })),
      api.get("/agents").catch(() => ({ data: [] })),
      api.get("/agent/actions").catch(() => ({ data: [] })),
    ]);
    setStatus(s.data); setAgents(asArray(a.data)); setActions(asArray(ac.data));
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);
  useEffect(() => { api.get("/clients").then(r => setCompanies(asArray(r.data))).catch(() => setCompanies([])); }, []);
  useEffect(() => {
    setSiteId("");
    if (!clientId) { setBranches([]); return; }
    api.get("/sites", { params: { client_id: clientId } }).then(r => setBranches(asArray(r.data))).catch(() => setBranches([]));
  }, [clientId]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/agents/install-token", {
        client_id: clientId || undefined,
        site_id: siteId || undefined,
        batch,
      });
      setBundle(data);
      toast.success(`Installer generated · ${batch ? "reusable (multi-endpoint)" : "single-use"} · ${data.expires_days}d valid`);
    } catch (e) {
      toast.error("Failed to generate installer");
    } finally { setGenerating(false); }
  };

  const downloadBinary = () => {
    const dl = bundle?.downloads || {};
    const url = platform === "macos" ? (macArch === "amd64" ? dl.macos_amd64 : dl.macos_arm64) : dl[platform];
    if (url) window.open(url, "_blank");
  };

  const pushAction = async () => {
    if (!pickedAgent) { toast.error("Pick an agent first"); return; }
    const r = await api.post(`/agent/${pickedAgent}/action`, action);
    toast.success(`Action ${r.data.delivered ? "delivered live" : "queued for next reconnect"}`);
    load();
  };

  const scopeLabel = clientId
    ? `${companies.find(c => c.id === clientId)?.name || "Company"}${siteId ? " · " + (branches.find(b => b.id === siteId)?.name || "Branch") : " · all branches"}`
    : "Tenant-wide (all companies)";
  const installCmd = bundle?.install_commands?.[platform];

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Agent Deployment</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Install &amp; Enroll Endpoints</h1>
        <p className="text-sm text-zinc-500 mt-1">Windows, macOS &amp; Linux. Generate a company-scoped installer for batch rollout — one installer, many endpoints — or a single-use token per machine. WebSocket telemetry · 60s heartbeat · auto-remediation.</p>
      </div>

      {status && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI label="Live Connections" value={status.live_connections} tone="good" testid="agent-live"/>
          <KPI label="Online" value={status.online} tone="good" testid="agent-online"/>
          <KPI label="Offline" value={status.offline} testid="agent-offline"/>
          <KPI label="Stale (5m)" value={status.stale_5m} tone={status.stale_5m > 0 ? "warn":"default"} testid="agent-stale"/>
        </div>
      )}

      {/* Deployment builder */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
        <div className="flex items-center gap-2 mb-4"><TerminalSquare size={14} className="text-blue-400"/><div className="font-display font-medium">Build Installer</div></div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Scope */}
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Company</div>
              <select value={clientId} onChange={e => setClientId(e.target.value)} data-testid="deploy-company" className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
                <option value="">All companies (tenant-wide)</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Branch / Location</div>
              <select value={siteId} onChange={e => setSiteId(e.target.value)} disabled={!clientId} data-testid="deploy-branch" className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm disabled:opacity-50">
                <option value="">{clientId ? "All branches" : "Select a company first"}</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer select-none">
              <input type="checkbox" checked={batch} onChange={e => setBatch(e.target.checked)} data-testid="deploy-batch" className="accent-blue-600" />
              Reusable installer (deploy to many endpoints)
            </label>
          </div>

          {/* Platform */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Platform</div>
            <div className="space-y-2">
              {PLATFORMS.map(p => {
                const Icon = p.icon; const active = platform === p.key;
                return (
                  <button key={p.key} onClick={() => setPlatform(p.key)} data-testid={`deploy-platform-${p.key}`}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded border text-left transition-colors ${active ? "bg-blue-600/15 border-blue-600/40 text-zinc-50" : "bg-zinc-900/40 border-zinc-800 text-zinc-300 hover:border-zinc-700"}`}>
                    <Icon size={16} className={active ? "text-blue-400" : "text-zinc-500"} />
                    <div><div className="text-sm font-medium">{p.label}</div><div className="text-[10px] text-zinc-500">{p.hint}</div></div>
                  </button>
                );
              })}
            </div>
            {platform === "macos" && (
              <div className="mt-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">macOS architecture</div>
                <div className="flex rounded border border-zinc-800 overflow-hidden text-sm">
                  {[["arm64", "Apple Silicon"], ["amd64", "Intel"]].map(([a, lbl]) => (
                    <button key={a} onClick={() => setMacArch(a)} data-testid={`deploy-macarch-${a}`}
                      className={`flex-1 px-3 py-1.5 transition-colors ${macArch === a ? "bg-blue-600/20 text-zinc-50" : "bg-zinc-900/40 text-zinc-400 hover:text-zinc-200"}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">The one-line installer auto-detects arch; this only affects the manual download.</div>
              </div>
            )}
          </div>

          {/* Summary + generate */}
          <div className="flex flex-col">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Target</div>
            <div className="bg-zinc-900/40 border border-zinc-900 rounded p-3 text-sm flex-1">
              <div className="text-zinc-300">{scopeLabel}</div>
              <div className="text-[11px] text-zinc-500 mt-1">{batch ? "Reusable · 30-day validity" : "Single-use · 7-day validity"}</div>
              <div className="text-[11px] text-zinc-500 mt-1">Endpoints auto-assign to this company/branch on enroll.</div>
            </div>
            <button onClick={generate} disabled={generating} data-testid="deploy-generate" className="mt-3 w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm rounded px-3 py-2">
              {generating ? "Generating…" : "Generate Installer"}
            </button>
          </div>
        </div>

        {/* Result */}
        {bundle && (
          <div className="mt-5 pt-5 border-t border-zinc-900 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm text-zinc-300">
                Installer for <span className="font-medium text-zinc-100">{PLATFORMS.find(p => p.key === platform)?.label}</span>
                {bundle.company && <> · <span className="text-zinc-400">{bundle.company}</span></>}
                <span className="ml-2 text-[11px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-400">{bundle.batch ? "reusable" : "single-use"}</span>
              </div>
              <button onClick={downloadBinary} data-testid="deploy-download" className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-200 text-sm rounded px-3 py-1.5">
                <Download size={14} /> Download {PLATFORMS.find(p => p.key === platform)?.label} agent{platform === "macos" ? ` (${macArch === "amd64" ? "Intel" : "Apple Silicon"})` : ""}
              </button>
            </div>
            <CopyRow label={`One-line install (${platform === "windows" ? "elevated PowerShell" : "sudo shell"})`} value={installCmd} testid="deploy-install-cmd" />
            <CopyRow label="Manual deploy (MDM / GPO)" value={bundle.manual_commands?.[platform] || bundle.deploy_command} testid="deploy-manual-cmd" />
            <CopyRow label="Install token (single-use unless reusable)" value={bundle.install_token} testid="deploy-token" />
          </div>
        )}
      </div>

      {/* Remote action */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
        <div className="flex items-center gap-2 mb-3"><Send size={14} className="text-blue-400"/><div className="font-display font-medium">Push Remote Action</div></div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
          <select value={pickedAgent} onChange={e=>setPickedAgent(e.target.value)} data-testid="agent-picker" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
            <option value="">Select enrolled agent…</option>
            {agents.map(a => <option key={a.id} value={a.id}>{[a.assigned_user, a.hostname || a.id].filter(Boolean).join(" · ")} · {a.status}</option>)}
          </select>
          <select value={action.kind} onChange={e=>setAction({...action,kind:e.target.value})} className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
            {["powershell","cmd","restart_service","restart_machine","custom"].map(k=><option key={k}>{k}</option>)}
          </select>
          <input value={action.command} onChange={e=>setAction({...action,command:e.target.value})} data-testid="agent-action-cmd" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm font-mono" placeholder="Command"/>
        </div>
        <button onClick={pushAction} data-testid="agent-action-push" className="mt-3 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded px-4 py-2">Push action</button>
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-900 font-display font-medium">Enrolled Agents ({agents.length})</div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th className="text-left px-4 py-2">Owner</th><th className="text-left px-4 py-2">Hostname</th><th className="text-left px-4 py-2">OS</th><th className="text-left px-4 py-2">Version</th><th className="text-left px-4 py-2">Last HB</th><th className="text-left px-4 py-2">Status</th><th></th></tr></thead>
          <tbody>
            {agents.map(a => (
              <tr key={a.id} onClick={() => navigate(`/agents/${a.id}`)} data-testid="agent-row" className="border-b border-zinc-900 cursor-pointer hover:bg-zinc-900/40">
                <td className="px-4 py-2">
                  {a.assigned_user
                    ? a.assigned_user
                    : <span className="text-zinc-600 text-xs uppercase font-mono">no owner</span>}
                </td>
                <td className="px-4 py-2 font-mono text-zinc-400">{a.hostname || a.id}</td>
                <td className="px-4 py-2 text-zinc-400">{a.os}</td>
                <td className="px-4 py-2 font-mono text-xs">{a.version}</td>
                <td className="px-4 py-2 font-mono text-xs text-zinc-500">{a.last_heartbeat ? fmtTime(a.last_heartbeat) : "—"}</td>
                <td className="px-4 py-2"><StatusBadge status={a.status}/></td>
                <td className="px-4 py-2 text-zinc-600"><ChevronRight size={14}/></td>
              </tr>
            ))}
            {agents.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-sm text-zinc-500">No agents enrolled yet — build an installer above and deploy it to your endpoints.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-900 font-display font-medium">Recent Actions ({actions.length})</div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th className="text-left px-4 py-2">Action</th><th className="text-left px-4 py-2">Kind</th><th className="text-left px-4 py-2">Command</th><th className="text-left px-4 py-2">Delivered</th><th className="text-left px-4 py-2">Requested</th></tr></thead>
          <tbody>
            {actions.map(a => (
              <tr key={a.id} className="border-b border-zinc-900">
                <td className="px-4 py-2 font-mono text-xs text-zinc-500">{a.action_id}</td>
                <td className="px-4 py-2 text-zinc-300">{a.kind}</td>
                <td className="px-4 py-2 font-mono text-xs">{a.command}</td>
                <td className="px-4 py-2 text-xs"><span className={a.delivered?"text-emerald-400":"text-amber-400"}>{a.delivered?"live":"queued"}</span></td>
                <td className="px-4 py-2 font-mono text-xs text-zinc-500">{a.requested_at ? fmtTime(a.requested_at) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
